import React from 'react';
import { withStyles } from '@material-ui/core';
import styles from './Delivery.styles';
import FormView from 'components/FormView';
import DeliveryDetailsForm from './Delivery.form';
import IDeliveryDetailProperties, { IDeliveryDetailsHandle } from './Delivery.properties';
import { ActionBarContext } from 'utils/ActionBarContextProvider';
import { Cancel as CancelIcon, CheckCircle } from '@markinson/uicomponents-v2/SvgIcons';
import { useSnackBar } from 'components/common/SnackBars/SnackBar.hooks';
import { usePOContextSelector, usePODispatch } from '../PurchaseOrders.context';
import { useCalculatePODeliveryDetails, useFetchPODelivery, useSetPurchaseOrdersDeliveryQueryData, useUpdatePurchaseOrdersDelivery } from 'api/purchaseOrders/purchaseOrders';
import { PurchaseOrdersDeliveryCalculationFields } from '../PurchaseOrders.constants';
import { useAsyncClickHandler } from 'utils/hooks';
import { PurchaseOrdersActionTypes } from '../PurchaseOrders.actions';
import { hasChanges } from 'utils/utils';
import { objectifyAddressForPost } from 'api/utils';
import { IBasicLookupFieldHandle } from '@markinson/uicomponents-v2/BasicLookupField/BasicLookup.properties';

const Delivery = (props: IDeliveryDetailProperties, ref: React.Ref<IDeliveryDetailsHandle>) => {
    const { classes, formValues, destoryForm, resetForm, changeSelectedTab, isV2, isV3 } = props;
    const onCancelClicked = React.useRef(false);
    const freightCodeFieldRef = React.useRef<IBasicLookupFieldHandle>();
    const freightTypeFieldRef = React.useRef<IBasicLookupFieldHandle>();
    const siteCodeFieldRef = React.useRef<IBasicLookupFieldHandle>();
    const { setActionBar } = React.useContext(ActionBarContext);

    const { showSnackBar } = useSnackBar();
    const [internalDirty, setInternalDirty] = React.useState<boolean>(false);
    const params = new URLSearchParams(location.search);
    const purchaseOrderId = usePOContextSelector<'PurchaseOrder'>((state) => state.PurchaseOrder) || params.get('PurchaseOrder');
    const POSummary = usePOContextSelector<'SummaryPanelData'>((state) => state.SummaryPanelData);
    const [isFirstTime, setIsFirstTime] = React.useState<boolean>(true);

    const { data: DeliveryDetails, isLoading: purchaseOrdersDeliveryLoading, isFetching: isPurchaseOrdersDeliveryFetching } = useFetchPODelivery(purchaseOrderId);
    const calculatePODeliveryDetails = useCalculatePODeliveryDetails();
    const setPurchaseOrdersDeliveryQueryData = useSetPurchaseOrdersDeliveryQueryData();
    const { mutateAsync: updatePODeliveryMutateAsync } = useUpdatePurchaseOrdersDelivery();
    const contextDispatch = usePODispatch();

    const isDirty = React.useMemo(
        () => hasChanges(formValues, DeliveryDetails?.PurchaseOrderDeliveryDetails?.inlineObject) || internalDirty,
        [formValues, DeliveryDetails?.PurchaseOrderDeliveryDetails?.inlineObject]
    );

    React.useEffect(
        () => {
            setActionBarButtons();
        },
        []
    );

    React.useEffect(
        () => {
            if (isFirstTime && freightTypeFieldRef.current && (isV3 || isV2)) {
                setTimeout(
                    () => {
                        if (isV3 && freightCodeFieldRef.current) {
                            freightCodeFieldRef.current.focus();
                        } else if (isV2) {
                            freightTypeFieldRef.current.focus();
                        }
                        setIsFirstTime(false);
                    },
                    0
                );
            }
        },
        [freightCodeFieldRef.current, freightTypeFieldRef.current, isFirstTime, isV3, isV2]
    );

    React.useEffect(
        () => {
            if (isDirty) {
                contextDispatch({
                    [PurchaseOrdersActionTypes.setPreventChangeStatus]: true,
                });
            } else {
                contextDispatch({
                    [PurchaseOrdersActionTypes.setPreventChangeStatus]: false,
                });
            }
        },
        [isDirty]
    );

    const calculateLineDetail = React.useCallback(
        async (name, value) => {
            if (!purchaseOrderId || purchaseOrdersDeliveryLoading ||
                DeliveryDetails?.PurchaseOrderDeliveryDetails?.inlineObject?.[name] === value) {
                return null;
            }
            const response = await calculatePODeliveryDetails.mutateAsync({
                ...DeliveryDetails?.PurchaseOrderDeliveryDetails?.inlineObject,
                ...formValues,
                ...objectifyAddressForPost((formValues as any)?.DeliveryAddress, 'DeliveryAddress', ['Name', 'Line1', 'Line2', 'City', 'State', 'PostalCode', 'Country']),
                PurchaseOrder: purchaseOrderId,
                [name]: value,
                urlQueryParams: {
                    ChangedField: name
                }
            });

            if (response?.Status) {
                setPurchaseOrdersDeliveryQueryData(purchaseOrderId, {
                    Status: true,
                    PurchaseOrderDeliveryDetails: response?.PurchaseOrderDeliveryDetails
                });
                setInternalDirty(true);
            } else {
                resetForm();
            }

            return response;
        },
        [formValues, calculatePODeliveryDetails, purchaseOrderId, DeliveryDetails?.PurchaseOrderDeliveryDetails?.inlineObject, purchaseOrdersDeliveryLoading]
    );

    const formViewOnChange = React.useCallback(
        async (fieldValue, fieldName): Promise<void> => {

            if (onCancelClicked.current) {
                onCancelClicked.current = false;

                return;
            }

            if (fieldName) {

                const isValueChanged = PurchaseOrdersDeliveryCalculationFields.includes(fieldName) ? parseFloat(fieldValue) ? parseFloat(fieldValue) : 0 !== parseFloat(DeliveryDetails?.PurchaseOrderDeliveryDetails?.inlineObject[fieldName])
                    : fieldValue !== DeliveryDetails?.PurchaseOrderDeliveryDetails?.inlineObject[fieldName];

                const isInvalidQuantity = PurchaseOrdersDeliveryCalculationFields.includes(fieldName) && (fieldValue === null || fieldValue === '');

                if (isValueChanged) {
                    if (isInvalidQuantity) {
                        await calculateLineDetail(fieldName, 0);
                    } else {
                        const responnse = await calculateLineDetail(fieldName, fieldValue);
                        if (fieldName === 'SiteCode' && responnse?.Status) {
                            setTimeout(
                                () => {
                                    siteCodeFieldRef.current?.focus();
                                },
                                0
                            );
                        }
                    }
                }
            }
        },
        [calculateLineDetail, DeliveryDetails?.PurchaseOrderDeliveryDetails?.inlineObject, siteCodeFieldRef.current]
    );

    const onCancel = React.useCallback(
        (): void => {
            onCancelClicked.current = true;
            setInternalDirty(false);
            destoryForm();
            setPurchaseOrdersDeliveryQueryData(purchaseOrderId, {
                Status: true,
                PurchaseOrderDeliveryDetails: null
            });
            changeSelectedTab('Lines');
        },
        [onCancelClicked]
    );

    const ok = React.useCallback(
        async (data?: any) => {
            const response = await updatePODeliveryMutateAsync({
                ...DeliveryDetails?.PurchaseOrderDeliveryDetails?.inlineObject,
                ...formValues,
                ...objectifyAddressForPost((formValues as any)?.DeliveryAddress, 'DeliveryAddress', ['Name', 'Line1', 'Line2', 'City', 'State', 'PostalCode', 'Country']),
                ...data,
                PurchaseOrder: purchaseOrderId
            });

            if (response?.Status === true) {
                setPurchaseOrdersDeliveryQueryData(purchaseOrderId, {
                    Status: true,
                    PurchaseOrderDeliveryDetails: null
                });
                destoryForm();
                setInternalDirty(false);
                changeSelectedTab('Lines');
            }

            return response;
        },
        [purchaseOrderId, formValues, changeSelectedTab]
    );

    const { onClick: onOk } = useAsyncClickHandler(ok, !calculatePODeliveryDetails.isLoading);

    const setActionBarButtons = React.useCallback(
        (): void => {
            setActionBar({
                leftIcons: [],
                centerIcons: [],
                rightIcons: [
                    {
                        label: 'Cancel',
                        Icon: CancelIcon,
                        iconStyle: { fill: 'rgba(178, 0, 0, 1)' },
                        action: 'DeliveryDetailsCancel',
                        callBack: onCancel
                    },
                    {
                        label: 'OK',
                        Icon: CheckCircle,
                        action: 'DeliveryDetailsOk',
                        callBack: onOk
                    },
                ]
            });
        },
        [onOk, onCancel]
    );

    React.useImperativeHandle(
        ref,
        () => ({
            isChangesSavedOrDiscarded(): boolean {
                if (isDirty) {
                    showSnackBar({ variant: 'warning', message: 'Please save or discard changes.' });
                }

                return !isDirty;
            },
            onOk,
            onCancel
        }),
        [isDirty, onOk, onCancel]
    );

    async function onLookupValueChanged(fieldValue: any, fieldName: string): Promise<void> {
        if (fieldValue && fieldValue.Code) {
            if (fieldValue.Code.toUpperCase() !== DeliveryDetails?.PurchaseOrderDeliveryDetails?.inlineObject?.[fieldName].toUpperCase()) {
                await formViewOnChange(fieldValue.Code, fieldName);
            }
        }
    }

    async function noValidationLookup(fieldValue: any, currentDisplay: any, fieldName: string): Promise<void> {
        if (fieldValue) {
            onLookupValueChanged(fieldValue, fieldName);
        } else {
            if (currentDisplay) {
                await formViewOnChange(currentDisplay, fieldName);
            }
        }
    }

    return (
        <div className={classes.formContainer}>
            <FormView
                isLoading={purchaseOrdersDeliveryLoading || !POSummary || isPurchaseOrdersDeliveryFetching}
                formName={'DeliveryDetailsForm'}
                schema={DeliveryDetailsForm}
                includeTabsHeight={false}
                initialValues={DeliveryDetails?.PurchaseOrderDeliveryDetails?.inlineObject || {}}
                valuesSchema={DeliveryDetails?.PurchaseOrderDeliveryDetails?.schema || {}}
                entity={POSummary?.Warehouse || ''}
                operationMode={'EDIT'}
                fieldExtensions={{
                    SaveDeliveryDetail: {
                        disabled: !formValues?.SiteCode,
                    },
                    SiteCode: {
                        fieldRef: (Ref) => siteCodeFieldRef.current = Ref,
                        onSelectedItemChange: (value: any, displayValue: any) => (noValidationLookup(value, displayValue, 'SiteCode'))
                    },
                    FreightCode: {
                        fieldRef: (Ref) => freightCodeFieldRef.current = Ref,
                        onSelectedItemChange: (value: any) => (onLookupValueChanged(value, 'FreightCode'))
                    },
                    FreightType: {
                        fieldRef: (Ref) => freightTypeFieldRef.current = Ref
                    },
                    Warehouse: {
                        onSelectedItemChange: (value: any) => (onLookupValueChanged(value, 'Warehouse'))
                    }
                }}
            />
        </div>
    );
};

export default withStyles(styles, { index: 1 })(React.memo(React.forwardRef(Delivery)));
